import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Send, Search, MoreVertical, Phone, Video,
  MessageSquare, Circle, Check, CheckCheck, Smile, Paperclip
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPartnerId = searchParams.get('with');
  const initialName = searchParams.get('name');
  const deepLinkHandled = useRef(false);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (loading || !initialPartnerId || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const existing = conversations.find(c => c.partner_id === initialPartnerId);
    if (existing) {
      handleSelectConversation(existing);
      return;
    }
    // No prior thread — start a new conversation with this partner.
    const parts = (initialName || 'New contact').trim().split(' ');
    const synthetic = {
      partner_id: initialPartnerId,
      user: { first_name: parts[0] || 'New', last_name: parts.slice(1).join(' ') || '', role: '' },
      last_message: '',
      last_message_time: new Date().toISOString(),
      unread_count: 0,
    };
    setSelectedConversation(synthetic);
    axios.get(`${API}/api/messages/${initialPartnerId}`, getAuthHeader())
      .then((res) => setMessages(res.data))
      .catch(() => setMessages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, initialPartnerId, conversations]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/messages/conversations`, getAuthHeader());
      setConversations(res.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await axios.get(`${API}/api/messages/${conversation.partner_id}`, getAuthHeader());
      setMessages(res.data);
      setConversations(prev => prev.map(c => c.partner_id === conversation.partner_id ? { ...c, unread_count: 0 } : c));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/messages`, {
        receiver_id: selectedConversation.partner_id,
        content: newMessage.trim(),
      }, getAuthHeader());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setConversations(prev => {
        const exists = prev.some(c => c.partner_id === selectedConversation.partner_id);
        const base = exists
          ? prev.map(c => c.partner_id === selectedConversation.partner_id
              ? { ...c, last_message: newMessage.trim(), last_message_time: new Date().toISOString() }
              : c)
          : [{ ...selectedConversation, last_message: newMessage.trim(), last_message_time: new Date().toISOString(), unread_count: 0 }, ...prev];
        const conv = base.find(c => c.partner_id === selectedConversation.partner_id);
        return [conv, ...base.filter(c => c.partner_id !== selectedConversation.partner_id)];
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = conv.user ? `${conv.user.first_name} ${conv.user.last_name}` : '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const getDashboardPath = () => {
    if (user?.role === 'patient') return '/patient/dashboard';
    if (user?.role === 'agency') return '/agency/dashboard';
    return '/clinician/dashboard';
  };

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-[#020617] relative overflow-hidden" data-testid="messages-page">
      <div className="pointer-events-none fixed -top-32 left-1/3 w-[24rem] h-[24rem] rounded-full bg-cn-secondary/15 blur-[120px] animate-cn-aurora" />
      <div className="pointer-events-none fixed -bottom-40 right-1/4 w-[28rem] h-[28rem] rounded-full bg-cn-accent/10 blur-[140px] animate-cn-aurora" style={{ animationDelay: '5s' }} />

      {/* Header */}
      <header className="relative z-20 cn-glass border-b border-cn-border/60 sticky top-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(getDashboardPath())} className="p-2 rounded-full hover:bg-cn-border/40" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <CaraLoomLogo />
            <div className="hidden sm:flex items-center gap-2 ml-4 pl-4 border-l border-cn-border/60">
              <MessageSquare className="w-4 h-4 text-cn-primary" />
              <span className="text-sm font-semibold text-cn-text">Messages</span>
              {totalUnread > 0 && <Badge className="bg-cn-accent text-white text-[10px]">{totalUnread} unread</Badge>}
            </div>
          </div>
        </div>
      </header>

      {/* Two-pane */}
      <main className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
        <div className="cn-card overflow-hidden h-[calc(100vh-128px)] grid grid-cols-1 md:grid-cols-12 bg-white dark:bg-[#0F172A]">
          {/* List */}
          <div className={`md:col-span-4 lg:col-span-4 border-r border-cn-border/60 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-cn-border/60">
              <p className="cn-eyebrow">Inbox</p>
              <h2 className="cn-display text-xl mt-1">Conversations</h2>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                <Input
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-cn-bg dark:bg-white/5 border-cn-border/60 rounded-xl"
                  data-testid="search-conversations"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-7 h-7 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center mb-3">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <p className="cn-display text-base text-cn-text">{searchQuery ? 'No matches' : 'No messages yet'}</p>
                  <p className="text-xs text-cn-text-muted mt-1">{!searchQuery && 'Start a conversation from your care team.'}</p>
                </div>
              ) : (
                <div className="p-2">
                  {filteredConversations.map((conv) => {
                    const active = selectedConversation?.partner_id === conv.partner_id;
                    const fullName = conv.user ? `${conv.user.first_name} ${conv.user.last_name}` : 'Unknown';
                    return (
                      <button
                        key={conv.partner_id}
                        onClick={() => handleSelectConversation(conv)}
                        data-testid={`conversation-${conv.partner_id}`}
                        className={`w-full p-3 flex items-start gap-3 rounded-2xl text-left transition-all mb-1 ${
                          active ? 'bg-cn-primary/10' : 'hover:bg-cn-bg dark:hover:bg-white/5'
                        }`}
                      >
                        <div className="relative">
                          <Avatar className="w-11 h-11">
                            <AvatarFallback className="text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                              {getInitials(fullName)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cn-success opacity-50" />
                            <span className="relative inline-flex h-3 w-3 rounded-full bg-cn-success border-2 border-white dark:border-[#0F172A]" />
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-semibold text-cn-text dark:text-white truncate text-sm">{fullName}</h3>
                            <span className="text-[10px] text-cn-text-muted whitespace-nowrap">{formatTime(conv.last_message_time)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <p className="text-xs text-cn-text-muted truncate">{conv.last_message}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-cn-accent text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center px-1">{conv.unread_count}</Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-cn-text-muted/70 mt-0.5 capitalize">{conv.user?.role || 'User'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Thread */}
          <div className={`md:col-span-8 lg:col-span-8 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-cn-border/60 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedConversation(null)} className="md:hidden p-2 rounded-full hover:bg-cn-border/40">
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <Avatar className="w-11 h-11">
                      <AvatarFallback className="text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                        {getInitials(selectedConversation.user ? `${selectedConversation.user.first_name} ${selectedConversation.user.last_name}` : '')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-display font-bold text-cn-text dark:text-white">
                        {selectedConversation.user ? `${selectedConversation.user.first_name} ${selectedConversation.user.last_name}` : 'Unknown'}
                      </h3>
                      <p className="text-[11px] uppercase tracking-wider font-bold text-cn-success flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-cn-success animate-pulse-soft" /> Online
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="text-cn-text-muted hover:text-cn-primary rounded-full">
                      <Phone className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-cn-text-muted hover:text-cn-primary rounded-full">
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-cn-text-muted rounded-full">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <ScrollArea className="flex-1 p-5" data-testid="messages-container">
                  <div className="space-y-3">
                    {messages.map((msg, index) => {
                      const isOwn = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id || index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                            <div className={`px-4 py-3 rounded-2xl ${
                              isOwn
                                ? 'bg-cn-primary text-white rounded-br-md'
                                : 'bg-cn-bg dark:bg-white/5 text-cn-text dark:text-white border border-cn-border/60 rounded-bl-md'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <div className="flex items-center gap-1 mt-1 px-1">
                              <span className="text-[10px] text-cn-text-muted">{formatTime(msg.created_at)}</span>
                              {isOwn && (msg.read ? <CheckCheck className="w-3 h-3 text-cn-primary" /> : <Check className="w-3 h-3 text-cn-text-muted" />)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                <form onSubmit={handleSendMessage} className="p-4 border-t border-cn-border/60">
                  <div className="flex items-center gap-2 bg-cn-bg dark:bg-white/5 border border-cn-border/60 rounded-2xl p-1.5 focus-within:border-cn-primary/40 transition-colors">
                    <button type="button" className="p-2 text-cn-text-muted hover:text-cn-primary rounded-full">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 border-0 bg-transparent focus-visible:ring-0"
                      data-testid="message-input"
                      disabled={sending}
                    />
                    <button type="button" className="p-2 text-cn-text-muted hover:text-cn-primary rounded-full">
                      <Smile className="w-4 h-4" />
                    </button>
                    <Button type="submit" disabled={!newMessage.trim() || sending} className="cn-btn cn-btn-primary !rounded-xl !py-2 !px-4" data-testid="send-message-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8" data-testid="thread-empty">
                <div className="w-24 h-24 rounded-3xl text-white flex items-center justify-center mb-5 shadow-lg shadow-cn-primary/30" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                  <MessageSquare className="w-12 h-12" />
                </div>
                <h2 className="cn-display text-2xl text-cn-text dark:text-white">Your messages</h2>
                <p className="text-sm text-cn-text-muted max-w-sm mt-2">
                  Pick a conversation on the left, or start one from your care team in the dashboard.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
